import React, { useEffect, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../Breadcrumb';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useParams } from 'react-router-dom';
import { API_URL } from '../..';

export default function ViewTicketPage() {
    const { id } = useParams();
    const [singleData, setSingleData] = useState({});
    const [currentUser, setCurrentUser] = useState({});
    const [replyMessage, setReplyMessage] = useState('');
    const [replyFiles, setReplyFiles] = useState([]);
    const [editStatus, showEditStatus] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchSingle = async () => {
        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user'));
            setCurrentUser(user);
            const fetchApi = await fetch(`${API_URL}/api/ticket/${id}`, { method: 'GET' });
            if (!fetchApi.ok) throw new Error('Failed to fetch ticket');
            const response = await fetchApi.json();
            setSingleData(response.ticket);
            setError(null);
        } catch (error) {
            console.error('Error fetching ticket:', error);
            setError('Failed to load ticket details. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSingle();
    }, [id]);

    // Handle file selection for reply
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const validTypes = [
            'image/jpeg', 'image/png', 'image/gif',
            'application/pdf', 'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];
        
        const validFiles = files.filter(file => validTypes.includes(file.type));
        const filesWithPreview = validFiles.map(file => ({
            file,
            preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
            type: file.type,
            name: file.name
        }));
        
        setReplyFiles(prev => [...prev, ...filesWithPreview]);
    };

    // Remove file from reply preview
    const removeReplyFile = (index) => {
        setReplyFiles(prev => {
            const newFiles = [...prev];
            if (newFiles[index].preview) URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    // Handle reply submission
    const handleReplySubmit = async (e) => {
        e.preventDefault();
        if (!e.target.elements.message.value.trim()) {
            setError('Please enter a reply message');
            return;
        }

        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));
        const formData = new FormData();
        formData.append('message', e.target.elements.message.value);
        formData.append('ticket_id', id);
        formData.append('sender_id', user.id);
        formData.append('sender_type', user.role === 'admin' ? 'user' : user.role === 'owner' ? 'affiliate' : user.role);
        replyFiles.forEach((item) => formData.append('documents', item.file));

        try {
            const response = await fetch(`${API_URL}/api/ticket/reply`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) throw new Error('Failed to submit reply');
            await response.json();
            e.target.elements.message.value = '';
            setReplyFiles([]);
            await fetchSingle();
            setError(null);
        } catch (error) {
            console.error('Error submitting reply:', error);
            setError('Failed to submit reply. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Format timestamp
    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleString() : '';

    // Render attachments
    const onChangeStatus = async (value)=>{
        const user = JSON.parse(localStorage.getItem('user'));
        const apiCall = await fetch(API_URL+ '/api/ticket/update-status/'+id+'/'+user.id, {method:'PATCH', body:JSON.stringify({
            status: value
        }), headers:{
            'Content-Type':'application/json'
        }});
        const response = await apiCall.json();
        if(apiCall.status === 200){
            fetchSingle();
            showEditStatus(false)
        }
    }
    const renderAttachments = (docs) => {
        if (!docs || !Array.isArray(docs)) return null;
        return (
            <div className="mt-3">
                <strong className="text-muted">Attachments:</strong>
                <div className="row gy-3 mt-2">
                    {docs.map((docUrl, idx) => {
                        const fileName = docUrl.split('/').pop();
                        const isImage = /\.(png|jpg|jpeg|gif)$/i.test(fileName);
                        return (
                            <div key={idx} className="col-md-3">
                                <div className="attachment-card position-relative bg-light rounded p-2 shadow-sm">
                                    {isImage ? (
                                        <>
                                            <img src={docUrl} alt={fileName} className="img-fluid rounded" style={{ maxHeight: '120px', objectFit: 'cover' }} />
                                            <a href={docUrl} download={fileName} className="btn btn-sm btn-outline-primary position-absolute top-0 end-0 m-1">
                                                <Icon icon="bi:download" />
                                            </a>
                                        </>
                                    ) : (
                                        <div className="text-center">
                                            <Icon icon="bi:file-earmark-text" className="text-primary" style={{ fontSize: '2.5rem' }} />
                                            <p className="mb-2 small text-muted">{fileName}</p>
                                            <a href={docUrl} download={fileName} className="btn btn-sm btn-outline-primary">
                                                <Icon icon="bi:download" />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <MasterLayout>
            <Breadcrumb title="Ticket" />

            <div className="row gy-4">
                {/* Left Column - Ticket Details */}
                <div className="col-md-4">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-primary text-white rounded-top">
                            <h5 className="card-title mb-0" style={{color:'white'}}>Ticket Details</h5>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center"><div className="spinner-border" role="status"></div></div>
                            ) : (
                                <>
                                    <div className="mb-3 d-flex justify-content-between align-items-center">
                                        <strong>Status:</strong>
                                        {
                                            currentUser?.role != 'owner' ?
                                           <p onClick={()=>showEditStatus(!editStatus)} className={`badge ${singleData.status === 'Closed' ? 'bg-success' : 'bg-warning'} px-2 py-1`}>
                                            {singleData.status} <i className='ph ph-pencil'></i>
                                          </p>:
                                          <span className={`badge ${singleData.status === 'Closed' ? 'bg-success' : 'bg-warning'} px-2 py-1`}>
                                          {singleData.status}
                                      </span>
                                        }
                                        
                                    </div>
                                    {
                                            editStatus ===true ?
                                            <div className='col-md-12 mb-3'>
                                            <label htmlFor='select_status' className='form-label'>Select Ticket Status</label>
                                            <select id='select_status' value={singleData.status} onChange={(e)=>onChangeStatus(e.target.value)} className='form-control'>
                                                <option  value={'Open'}>Open</option>
                                                <option value={'In Progress'}>In Progress</option>
                                                <option value={'Resolved'}>Resolved</option>
                                                <option value={'Closed'}>Closed</option>
                                            </select>
                                            </div>
                                            :null
                                        }
                                    <div className="mb-3">
                                        <strong>Department:</strong>
                                        <span className="ms-2 text-muted">{singleData.department}</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong>Created At:</strong>
                                        <span className="ms-2 text-muted">{formatDate(singleData.createdAt)}</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong>Ticket ID:</strong>
                                        <span className="ms-2 text-muted">{singleData.id}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Ticket Subject and Replies */}
                <div className="col-md-8">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-light border-bottom">
                            <h5 className="card-title mb-0 text-primary">{singleData.subject}</h5>
                        </div>
                        <div className="card-body p-4">
                            {error && <div className="alert alert-danger mb-4">{error}</div>}
                            {loading ? (
                                <div className="text-center"><div className="spinner-border" role="status"></div></div>
                            ) : (
                                <>
                                    {/* Timeline/Chat View */}
                                    <div className="chat-container" style={{ maxHeight: '500px', overflowY: 'auto', background: '#f8f9fa', borderRadius: '8px', padding: '15px' }}>
                                        {/* Initial Ticket Entry */}
                                       

                                        {/* Replies */}
                                        {singleData.replies?.reverse().map((reply, index) => (
                                            <div key={reply.id} className="chat-message mb-4">
                                                <div className="d-flex align-items-start">
                                                    <div className="avatar rounded-circle me-3 bg-secondary text-white d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                                                        <Icon icon="bi:person" width="24" />
                                                    </div>
                                                    <div className="message-content bg-white p-3 rounded shadow-sm flex-grow-1">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <p className="mb-0 text-primary">{reply.reply_by.id === currentUser?.id ? 'You' : reply.reply_by.name}</p>
                                                            <small className="text-muted">{formatDate(reply.created_at)}</small>
                                                        </div>
                                                        <p className="mb-0">{reply.message}</p>
                                                        {renderAttachments(reply.documents)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                         <div className="chat-message mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="avatar rounded-circle me-3 bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                                                    <Icon icon="bi:person" width="24" />
                                                </div>
                                                <div className="message-content bg-white p-3 rounded shadow-sm flex-grow-1">
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <p className="mb-0 text-primary">{singleData.subject}</p>
                                                        <small className="text-muted">{formatDate(singleData.createdAt)}</small>
                                                    </div>
                                                    <p className="mb-0">{singleData.description}</p>
                                                    {renderAttachments(JSON.parse(singleData.documents || '[]'))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reply Form */}
                                    {singleData.status !== 'Closed'  && singleData.status !== 'Resolved'  ? (
                                        <div className="mt-4">
                                            <form onSubmit={handleReplySubmit}>
                                                <div className="mb-3">
                                                    <textarea
                                                        className="form-control border-primary"
                                                        // value={replyMessage}
                                                        name='message'
                                                        // onChange={(e) => setReplyMessage(e.target.value)}
                                                        placeholder="Type your reply..."
                                                        rows="3"
                                                        style={{ resize: 'none', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <input
                                                        type="file"
                                                        className="form-control border-primary"
                                                        multiple
                                                        accept="image/*,.pdf,.doc,.docx"
                                                        onChange={handleFileChange}
                                                    />
                                                    {replyFiles.length > 0 && (
                                                        <div className="row gy-3 mt-3">
                                                            {replyFiles.map((item, index) => (
                                                                <div key={index} className="col-md-3">
                                                                    <div className="attachment-preview position-relative bg-light p-2 rounded shadow-sm">
                                                                        {item.type.includes('image') ? (
                                                                            <img src={item.preview} alt={item.name} className="img-fluid rounded" style={{ maxHeight: '120px', objectFit: 'cover' }} />
                                                                        ) : (
                                                                            <div className="text-center">
                                                                                <Icon icon="bi:file-earmark-text" className="text-primary" style={{ fontSize: '2.5rem' }} />
                                                                                <p className="mb-2 small text-muted">{item.name}</p>
                                                                            </div>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                                                                            onClick={() => removeReplyFile(index)}
                                                                        >
                                                                            <Icon icon="bi:trash" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                                    {loading ? <span className="spinner-border spinner-border-sm me-2" role="status"></span> : null}
                                                    Send Reply
                                                </button>
                                            </form>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </MasterLayout>
    );
}